/**
 * The things every sheet in this product needs and each was getting
 * slightly differently.
 *
 * There are five of them — the tee sheet, the court sheet, the pool
 * deck, the range sheet and the dock sheet — and they are the same
 * screen with a different noun. Three complaints, all of them true of
 * all five:
 *
 * ---------------------------------------------------------------------
 * ONE — THEY OPEN AT MIDNIGHT
 *
 * A sheet runs from the first tee time to the last, and it opened at the
 * top. At two in the afternoon that is four hours of finished golf, and
 * the professional scrolls past all of it every single time. The morning
 * is not gone — it is history, and history is what you go and look for,
 * not what you are shown by default.
 *
 * So the sheet opens at the interval containing now, with one
 * interval of context above it, and a checkbox puts the whole day back.
 * `firstVisible` is that rule.
 *
 * Only for TODAY. Tomorrow has no "now" in it, and a sheet for next
 * Tuesday that hid its own morning would be hiding the entire day.
 *
 * ---------------------------------------------------------------------
 * TWO — A DATE DOES NOT SAY WHICH DAY IT IS
 *
 * "2026-09-05" is not a Saturday to anybody reading it, and Saturday is
 * the single most load-bearing fact about a tee sheet: it decides the
 * booking window, the guest policy and how many people are waiting. So
 * every sheet says the day of the week, and says "Today" or "Tomorrow"
 * where that is what it is.
 *
 * ---------------------------------------------------------------------
 * THREE — TODAY IS NOT UTC
 *
 * Each sheet had its own `today()` that built a UTC date and sliced ten
 * characters off it. West of Greenwich that is tomorrow's sheet from
 * seven in the evening — Rockford is UTC-5, so the professional closing
 * up at 7pm gets tomorrow's empty sheet and concludes the bookings have
 * been lost.
 *
 * The club's own timezone is on the session. Everything here takes it.
 */

/** Today, in the club's timezone rather than the browser's or UTC's. */
export function clubToday(timezone: string | undefined): string {
  return isoIn(new Date(), timezone);
}

/** A instant as a YYYY-MM-DD in a named zone. */
export function isoIn(at: Date, timezone: string | undefined): string {
  try {
    // 'en-CA' because its short date format IS YYYY-MM-DD. Building the
    // string from parts is the alternative and it is four lines of
    // padStart that do the same thing.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(at);
  } catch {
    // An unrecognized zone must not take the screen down with it. The
    // browser's own idea of today is wrong by at most a day and is
    // always a real date.
    return new Intl.DateTimeFormat('en-CA').format(at);
  }
}

/** Minutes since midnight, in the club's timezone. 14:30 → 870. */
export function clubMinutesNow(timezone: string | undefined): number {
  let hh = 0;
  let mm = 0;
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone || undefined,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    hh = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
    mm = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  } catch {
    const now = new Date();
    hh = now.getHours();
    mm = now.getMinutes();
  }
  // 24:00 is what some locales render midnight as. Left as 24 it makes
  // every slot in the day "past" and the sheet opens empty.
  return (hh % 24) * 60 + mm;
}

/** Shift a plain YYYY-MM-DD by whole days without going near a timezone. */
export function shiftDate(iso: string, days: number): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  const at = new Date(Date.UTC(y, m - 1, d));
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/**
 * What to call the day this sheet is for.
 *
 * "Saturday · Today" or "Sunday · Tomorrow" where that applies, and the
 * weekday with the date where it does not. Both halves matter: the
 * weekday is the rule-bearing fact, and Today/Tomorrow is what somebody
 * checks before they believe the sheet in front of them is the one they
 * meant to open.
 */
export function dayLabel(iso: string, timezone: string | undefined): string {
  const today = clubToday(timezone);
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;

  // Formatted from a UTC-noon instant, so no zone can round it to the
  // day before. The string is a plain date; it is not an instant and
  // must not be treated as one.
  const weekday = new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d, 12)));

  if (iso === today) return `${weekday} · Today`;
  if (iso === shiftDate(today, 1)) return `${weekday} · Tomorrow`;
  if (iso === shiftDate(today, -1)) return `${weekday} · Yesterday`;

  const rest = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    // The year only when it is not this one. "Saturday, Sep 5, 2026" on
    // every sheet all year is three characters of noise on the line
    // somebody reads most often.
    year: y === Number(today.slice(0, 4)) ? undefined : 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d, 12)));
  return `${weekday}, ${rest}`;
}

/**
 * Where the sheet should start.
 *
 * Returns the index of the first row to show. `-1` means show
 * everything, which is what a sheet for any day but today does, and what
 * the "from the start of the day" checkbox does for today.
 *
 * `startsAt` is whatever each sheet calls its time: 'HH:MM', 'HH:MM:SS',
 * or a full timestamp. Only the first five characters are read, because
 * every one of those five formats agrees on them and parsing a timestamp
 * would drag a timezone into a comparison that has none.
 *
 * One row of context above the current interval, deliberately. Opening
 * exactly on the current slot puts the group teeing off right now at the
 * very top edge with nothing above it, and the desk's next question is
 * almost always about the group that has just gone out.
 */
export function firstVisible(
  rows: { startsAt: string }[],
  sheetDate: string,
  timezone: string | undefined,
  showWholeDay: boolean,
): number {
  if (showWholeDay) return -1;
  if (sheetDate !== clubToday(timezone)) return -1;

  const now = clubMinutesNow(timezone);
  const at = rows.findIndex((r) => minutesOf(r.startsAt) >= now);

  // Nothing left today: the day is over, so show it whole rather than
  // showing an empty sheet at six in the evening.
  if (at === -1) return -1;
  return Math.max(at - 1, 0);
}

/** 'HH:MM…' → minutes since midnight. */
export function minutesOf(time: string): number {
  // A full timestamp carries the time after the 'T'; a bare time is
  // already at the front.
  const hhmm = time.includes('T') ? time.slice(11, 16) : time.slice(0, 5);
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** 'HH:MM:SS' or a timestamp → 'HH:MM'. What a sheet actually prints. */
export function hhmm(time: string): string {
  return time.includes('T') ? time.slice(11, 16) : time.slice(0, 5);
}
